// @ts-nocheck
/**
 * MarketTab - マーケット情報タブ
 *
 * カードの市場価格、価格履歴、変動要因を表示
 */

import React, { useMemo } from 'react';
import { COLORS } from '../../../styles/cardDexStyles';
import { calculateCardValue } from '../../../../systems/valueCalculator';
import { getCardMarketPrice } from '../../../../market/marketEngine';
import { RARITY_MULTIPLIERS, RARITY_COLORS, RARITIES } from '../../../../data/constants';
import { PriceHistoryPanel } from '../../../../components/PriceHistoryPanel';

// ========================================
// 現在価格セクション
// ========================================

const CurrentPriceSection = ({ baseValue, marketPrice, modifier, sellPrice }) => {
  const isPositive = modifier >= 0;
  const modifierColor = isPositive ? COLORS.textSuccess : COLORS.textDanger;
  const modifierSign = isPositive ? '+' : '';

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 現在価格</div>
      <div style={sectionStyles.content}>
        <div style={priceStyles.row}>
          <span style={priceStyles.label}>基準価格:</span>
          <span style={priceStyles.value}>{baseValue.toLocaleString()} G</span>
        </div>
        <div style={priceStyles.row}>
          <span style={priceStyles.label}>市場価格:</span>
          <span style={{ ...priceStyles.value, color: COLORS.textGold, fontWeight: 'bold' }}>
            {marketPrice.toLocaleString()} G
          </span>
          <span style={{ color: modifierColor, marginLeft: '8px', fontSize: '13px' }}>
            ({modifierSign}{modifier.toFixed(1)}%) {isPositive ? '▲' : '▼'}
          </span>
        </div>
        <div style={priceStyles.row}>
          <span style={priceStyles.label}>売却価格:</span>
          <span style={{ ...priceStyles.value, color: COLORS.textSecondary }}>
            {sellPrice.toLocaleString()} G (50%)
          </span>
        </div>
      </div>
    </div>
  );
};

// ========================================
// 価格変動要因セクション
// ========================================

const PriceFactorsSection = ({ breakdown }) => {
  if (!breakdown || breakdown.length === 0) {
    return (
      <div style={sectionStyles.container}>
        <div style={sectionStyles.title}>■ 価格変動要因</div>
        <div style={{ ...sectionStyles.content, color: COLORS.textSecondary }}>
          現在変動要因はありません
        </div>
      </div>
    );
  }

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 価格変動要因</div>
      <div style={sectionStyles.content}>
        {breakdown.map((factor, index) => (
          <div key={index} style={factorStyles.row}>
            <span style={factorStyles.source}>{factor.source}</span>
            <span
              style={{
                ...factorStyles.modifier,
                color: factor.modifier.startsWith('+') ? COLORS.textSuccess : COLORS.textDanger,
              }}
            >
              {factor.modifier}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========================================
// 価格履歴チャートセクション（サーバーベース）
// ========================================

const PriceHistorySection = ({ cardId, rarityMultiplier }) => {
  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ 価格履歴</div>
      <PriceHistoryPanel
        cardId={cardId}
        rarityMultiplier={rarityMultiplier}
        chartHeight={120}
      />
    </div>
  );
};

// ========================================
// レアリティ別価格セクション
// ========================================

const RarityPricesSection = ({ baseValue, modifier }) => {
  // 市場価格を適用した各レアリティの価格
  const rarityPrices = useMemo(() => {
    const marketMultiplier = 1 + modifier / 100;
    return RARITIES.map((rarity) => {
      const rarityMultiplier = RARITY_MULTIPLIERS[rarity] || 1;
      const price = Math.round(baseValue * rarityMultiplier * marketMultiplier);
      return { rarity, price };
    });
  }, [baseValue, modifier]);

  return (
    <div style={sectionStyles.container}>
      <div style={sectionStyles.title}>■ レアリティ別価格</div>
      <div style={rarityStyles.grid}>
        {rarityPrices.map(({ rarity, price }) => (
          <div key={rarity} style={rarityStyles.item}>
            <span style={{ color: RARITY_COLORS[rarity], fontWeight: 'bold' }}>{rarity}:</span>
            <span style={rarityStyles.price}>{price.toLocaleString()} G</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========================================
// メインコンポーネント
// ========================================

const MarketTab = ({ card, playerData, selectedRarity }) => {
  const marketState = playerData?.marketState;

  // レアリティ倍率
  const rarityMultiplier = RARITY_MULTIPLIERS[selectedRarity] || 1;

  // 価格計算
  const priceData = useMemo(() => {
    if (!card) return null;

    // 基礎価値を取得
    const valueInfo = calculateCardValue(card);
    const baseValue = valueInfo.baseValue;

    // 選択中のレアリティで価格計算
    const rarityBaseValue = Math.round(baseValue * rarityMultiplier);

    // 市場価格を取得
    if (marketState) {
      const result = getCardMarketPrice(card, rarityBaseValue, marketState, selectedRarity, valueInfo.tier);
      return {
        baseValue: rarityBaseValue,
        marketPrice: result.price,
        modifier: result.modifier,
        breakdown: result.breakdown || [],
        sellPrice: Math.round(result.price * 0.5),
      };
    }

    // marketStateがない場合は基準価格のみ
    return {
      baseValue: rarityBaseValue,
      marketPrice: rarityBaseValue,
      modifier: 0,
      breakdown: [],
      sellPrice: Math.round(rarityBaseValue * 0.5),
    };
  }, [card, marketState, selectedRarity, rarityMultiplier]);

  if (!card) {
    return (
      <div style={{ color: COLORS.textSecondary, textAlign: 'center', padding: '20px' }}>
        カード情報が見つかりません
      </div>
    );
  }

  if (!priceData) {
    return (
      <div style={{ color: COLORS.textSecondary, textAlign: 'center', padding: '20px' }}>
        価格データを計算中...
      </div>
    );
  }

  // 基礎価値（レアリティ倍率なし）
  const valueInfo = calculateCardValue(card);
  const pureBaseValue = valueInfo.baseValue;

  return (
    <div>
      {/* 選択中レアリティ表示 */}
      <div style={{ marginBottom: '16px', fontSize: '13px', color: COLORS.textSecondary }}>
        表示レアリティ:{' '}
        <span style={{ color: RARITY_COLORS[selectedRarity], fontWeight: 'bold' }}>{selectedRarity}</span>
      </div>

      {/* 現在価格 */}
      <CurrentPriceSection
        baseValue={priceData.baseValue}
        marketPrice={priceData.marketPrice}
        modifier={priceData.modifier}
        sellPrice={priceData.sellPrice}
      />

      {/* 価格変動要因 */}
      <PriceFactorsSection breakdown={priceData.breakdown} />

      {/* 価格履歴チャート（サーバーベース） */}
      <PriceHistorySection cardId={card.id} rarityMultiplier={rarityMultiplier} />

      {/* レアリティ別価格 */}
      <RarityPricesSection baseValue={pureBaseValue} modifier={priceData.modifier} />

      {/* 注意事項 */}
      <div style={{ marginTop: '16px', fontSize: '11px', color: COLORS.textSecondary }}>
        ※ 価格は市場の需給によって変動します。履歴はサーバーから取得されます。
      </div>
    </div>
  );
};

// ========================================
// スタイル定義
// ========================================

const sectionStyles = {
  container: {
    marginBottom: '20px',
  },
  title: {
    fontSize: '14px',
    color: COLORS.textGold,
    marginBottom: '12px',
  },
  content: {
    padding: '12px',
    background: 'rgba(30, 30, 50, 0.6)',
    borderRadius: '8px',
  },
};

const priceStyles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  label: {
    width: '100px',
    fontSize: '13px',
    color: COLORS.textSecondary,
  },
  value: {
    fontSize: '14px',
    color: COLORS.textPrimary,
  },
};

const factorStyles = {
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: `1px solid ${COLORS.borderSecondary}`,
  },
  source: {
    fontSize: '13px',
    color: COLORS.textPrimary,
  },
  modifier: {
    fontSize: '13px',
    fontWeight: 'bold',
  },
};

const rarityStyles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '8px',
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 10px',
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
    fontSize: '12px',
  },
  price: {
    color: COLORS.textPrimary,
  },
};

export default MarketTab;
